// src/components/CreateLoadRequestModal.tsx
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Form, Select, Upload, Button, message, Space, Typography, Divider, Collapse } from 'antd';
import { UploadOutlined, InboxOutlined, UserAddOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { getAndSanitize } from '../shared/utils/sanitization';
import { useTheme } from '../contexts/ThemeContext';
import { usePanelsStore } from '../shared/stores/usePanelsStore';
import { CreateUserModal } from './CreateUserModal';
import type { UploadFile, UploadProps } from 'antd/lib/upload';

const { Text } = Typography;
const { Dragger } = Upload;

interface CreateLoadRequestModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

interface CreateLoadRequestFormData {
  username: string;
  receipt: File;
  panelId?: string;
  panelEndpointId?: string;
  newAccountName?: string;
  newAccountId?: string;
}

interface PanelEndpoint {
  id: string;
  name: string;
  baseUrl: string;
  status: string;
}

interface PanelUser {
  id: string;
  username: string;
  accountName?: string;
  userId?: string;
  userNumber?: string;
  phone?: string;
}

interface Panel {
  id: string;
  name?: string;
  isActive?: boolean;
}

interface UsersResponse {
  success: boolean;
  data: PanelUser[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export const CreateLoadRequestModal: React.FC<CreateLoadRequestModalProps> = ({
  open,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [availablePanels, setAvailablePanels] = useState<Panel[]>([]);
  const [selectedPanelId, setSelectedPanelId] = useState<string | null>(null);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);

  // Estados para PanelEndpoint opcional
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);
  const [newAccountName, setNewAccountName] = useState<string>('');
  const [newAccountId, setNewAccountId] = useState<string>('');

  // Estados para búsqueda y paginación de usuarios
  const [userSearchText, setUserSearchText] = useState<string>('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState<string>('');
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [allLoadedUsers, setAllLoadedUsers] = useState<PanelUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce effect para búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      if (userSearchText !== debouncedUserSearch) {
        setDebouncedUserSearch(userSearchText);
        setCurrentUserPage(1);
        setAllLoadedUsers([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [userSearchText, debouncedUserSearch]);

  // Reset cuando se abre/cierra el modal
  useEffect(() => {
    if (open) {
      loadPanels();
      resetUserSearch();
    } else {
      resetUserSearch();
    }
  }, [open]);

  const resetUserSearch = useCallback(() => {
    setUserSearchText('');
    setDebouncedUserSearch('');
    setCurrentUserPage(1);
    setAllLoadedUsers([]);
    setIsSearching(false);
  }, []);

  const loadPanels = async () => {
    try {
      setIsLoadingPanels(true);
      const panels = await usePanelsStore.getState().fetchPanels();
      setAvailablePanels(panels);
      
      if (panels.length === 1) {
        setSelectedPanelId(panels[0].id);
      } else if (panels.length === 0) {
        message.error('No panels available');
      }
    } catch (error) {
      console.error('[CreateLoadRequest] Error loading panels:', error);
      message.error('Failed to load panels');
    } finally {
      setIsLoadingPanels(false);
    }
  };

  // Query para obtener usuarios
  const { data: usersResponse, isLoading: isLoadingUsers, error: usersError } = useQuery({
    queryKey: ['panel-users-search', debouncedUserSearch, currentUserPage],
    queryFn: async (): Promise<UsersResponse> => {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) {
        throw new Error('Authentication required');
      }

      const params: Record<string, string | number> = {
        page: currentUserPage,
        limit: 20
      };

      // Agregar parámetros de búsqueda
      if (debouncedUserSearch.trim()) {
        const searchTerm = debouncedUserSearch.trim();
        if (/^\d+$/.test(searchTerm)) {
          params.userId = searchTerm;
        } else {
          params.username = searchTerm;
        }
      }

      const response = await axios.get<UsersResponse>(`${API_BASE_URL}/api/panel/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-ID': adminId
        },
        params
      });

      return response.data;
    },
    enabled: open,
    staleTime: 5000,
    gcTime: 30000,
    refetchOnWindowFocus: false,
    retry: 2
  });

  // Query para obtener PanelEndpoints (reutiliza el cache de CreateUserModal)
  const { data: panelEndpoints = [] } = useQuery({
    queryKey: ['panel-endpoints'],
    queryFn: async (): Promise<PanelEndpoint[]> => {
      const token = getAndSanitize('token');

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.get<{ success: boolean; data: PanelEndpoint[] }>(
        `${API_BASE_URL}/api/auth/endpoints`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data.data;
    },
    enabled: open,
    staleTime: 30000,
    gcTime: 60000,
    refetchOnWindowFocus: false
  });

  const isLoadingEndpoints = false; // Query ya tiene datos cacheados

  // Effect para manejar la respuesta de usuarios
  useEffect(() => {
    if (usersResponse?.data) {
      setAllLoadedUsers(prevUsers => {
        if (currentUserPage === 1) {
          // Primera página o nueva búsqueda: reemplazar usuarios
          return usersResponse.data;
        } else {
          // Páginas adicionales: agregar usuarios
          const existingIds = new Set(prevUsers.map(user => user.id));
          const newUsers = usersResponse.data.filter(user => !existingIds.has(user.id));
          return [...prevUsers, ...newUsers];
        }
      });
      
      setIsSearching(false);
    }
  }, [usersResponse, currentUserPage]);

  // Effect para manejar errores
  useEffect(() => {
    if (usersError) {
      console.error('[CreateLoadRequest] Users query error:', usersError);
      setIsSearching(false);
    }
  }, [usersError]);

  const loadMoreUsers = useCallback(() => {
    if (!isLoadingUsers && usersResponse?.hasMore) {
      setCurrentUserPage(prev => prev + 1);
    }
  }, [isLoadingUsers, usersResponse?.hasMore, currentUserPage]);

  const handleUserSearch = useCallback((value: string) => {
    setUserSearchText(value);
    setIsSearching(true);
  }, []);

  // Preparar opciones para el Select
const userSelectOptions = useMemo(() => {
  const options = allLoadedUsers.map(user => ({
    value: user.username,
    label: (
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <Text strong style={{ fontSize: 14 }}>{user.username}</Text>
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
          {[
            user.accountName && `Account: ${user.accountName}`,
            user.userId && `ID: ${user.userId}`,
            user.userNumber && `#${user.userNumber}`,
            user.phone && `Phone: ${user.phone}`
          ].filter(Boolean).join(' | ')}
        </Text>
      </div>
    ),
    searchValue: [
      user.username,
      user.accountName,
      user.userId,
      user.userNumber,
      user.phone
    ].filter(Boolean).join(' ').toLowerCase()
  }));

  return options;
}, [allLoadedUsers]);

  const createLoadRequestMutation = useMutation({
    mutationFn: async (formData: CreateLoadRequestFormData) => {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) {
        throw new Error('Authentication required');
      }

      const panelId = formData.panelId || selectedPanelId;
      if (!panelId) {
        throw new Error('Panel selection required');
      }

      if (!formData.receipt) {
        throw new Error('Receipt file is required');
      }

      const formDataPayload = new FormData();
      formDataPayload.append('username', formData.username);
      formDataPayload.append('adminId', adminId);
      formDataPayload.append('panelId', panelId);
      formDataPayload.append('receipt', formData.receipt);

      // Agregar campos opcionales de cuenta si se seleccionó un endpoint
      if (formData.panelEndpointId) {
        formDataPayload.append('panelEndpointId', formData.panelEndpointId);
      }
      if (formData.newAccountName) {
        formDataPayload.append('newAccountName', formData.newAccountName);
      }
      if (formData.newAccountId) {
        formDataPayload.append('newAccountId', formData.newAccountId);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/requests/load-request`,
        formDataPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            'X-Admin-ID': adminId
          }
        }
      );

      return response.data;
    },
    onSuccess: async () => {
      // ✅ FIX: Confiar en websocket + store debounced refetch
      // 600ms da tiempo a websocket (50-100ms) + store debounce (500ms) = actualización completa
      await new Promise(resolve => setTimeout(resolve, 600));

      message.success(t('loadRequest.create.success') || 'Load request created successfully');

      form.resetFields();
      setFileList([]);
      setSelectedPanelId(null);
      setSelectedEndpointId(null);
      setNewAccountName('');
      setNewAccountId('');
      resetUserSearch();

      onSuccess?.();
      onCancel();
    },
    onError: (error: any) => {
      console.error('[CreateLoadRequest] Error:', error);
      message.error(
        error.response?.data?.message || 
        t('loadRequest.create.error') || 
        'Failed to create load request'
      );
    }
  });

  const handleSubmit = (values: { username: string }) => {
    if (fileList.length === 0) {
      message.error(t('validation.required.receipt') || 'Receipt file is required');
      return;
    }

    const panelId = selectedPanelId;
    if (!panelId) {
      message.error('Please select a panel');
      return;
    }

    const uploadFile = fileList[0];
    let actualFile: File;

    if (uploadFile.originFileObj) {
      actualFile = uploadFile.originFileObj as File;
    } else if (uploadFile instanceof File) {
      actualFile = uploadFile;
    } else {
      message.error('Invalid file format');
      return;
    }

    createLoadRequestMutation.mutate({
      username: values.username,
      receipt: actualFile,
      panelId,
      panelEndpointId: selectedEndpointId || undefined,
      newAccountName: newAccountName || undefined,
      newAccountId: newAccountId || undefined
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setSelectedPanelId(null);
    setPendingUsername(null);
    setSelectedEndpointId(null);
    setNewAccountName('');
    setNewAccountId('');
    resetUserSearch();
    onCancel();
  };

  const handleCreateUserSuccess = useCallback(async () => {
    // ✅ FIX: Confiar en websocket para actualizar datos
    // 600ms da tiempo al websocket y actualizaciones del store
    await new Promise(resolve => setTimeout(resolve, 600));

    // Si había un username pendiente, seleccionarlo
    if (pendingUsername) {
      form.setFieldsValue({ username: pendingUsername });
      setPendingUsername(null);
    }

    setCreateUserModalOpen(false);
    message.success('User created successfully and selected');

    // Reset de búsqueda para recargar la lista
    resetUserSearch();
  }, [pendingUsername, form, resetUserSearch]);

  const handleCreateNewUser = useCallback(() => {
    const currentUsername = form.getFieldValue('username');
    if (currentUsername && typeof currentUsername === 'string') {
      setPendingUsername(currentUsername);
    }
    setCreateUserModalOpen(true);
  }, [form]);

  const uploadProps: UploadProps = {
    name: 'receipt',
    multiple: false,
    fileList: fileList,
    beforeUpload: (file) => {
      const isImage = file.type?.startsWith('image/') || false;
      const isPDF = file.type === 'application/pdf';
      
      if (!isImage && !isPDF) {
        message.error(t('validation.fileType.receipt') || 'Only image and PDF files are allowed');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('validation.fileSize.receipt') || 'File must be smaller than 10MB');
        return false;
      }

      const uploadFile: UploadFile = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      
      setFileList([uploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
    }
  };

  return (
    <>
      <Modal
        title={t('loadRequest.create.title') || 'Create Load Request'}
        open={open}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {availablePanels.length > 1 && (
            <Form.Item
              label={t('common.panel') || 'Panel'}
              required
            >
              <Select
                placeholder={t('common.selectPanel') || 'Select a panel'}
                value={selectedPanelId}
                onChange={setSelectedPanelId}
                loading={isLoadingPanels}
                options={availablePanels.map(panel => ({
                  value: panel.id,
                  label: (
                    <Space>
                      <Text>{panel.name || panel.id}</Text>
                      {panel.isActive && <Text type="success">(Active)</Text>}
                    </Space>
                  )
                }))}
              />
            </Form.Item>
          )}

          {availablePanels.length === 1 && (
            <Form.Item>
              <Text type="secondary">
                Panel: {availablePanels[0].name || availablePanels[0].id}
              </Text>
            </Form.Item>
          )}

          <Form.Item
            name="username"
            label={
              <Space>
                <span>{t('common.selectUser') || 'Select User'}</span>
                {(isLoadingUsers || isSearching) && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (Searching...)
                  </Text>
                )}
              </Space>
            }
            rules={[
              { required: true, message: t('validation.required.user') || 'User selection is required' }
            ]}
          >
            <Select
              placeholder={t('loadRequest.create.selectUserPlaceholder') || 'Search and select a user'}
              showSearch
              searchValue={userSearchText}
              onSearch={handleUserSearch}
              filterOption={(input, option) => {
                if (!option?.searchValue) return false;
                return option.searchValue.toLowerCase().includes(input.toLowerCase());
              }}
              loading={isLoadingUsers && currentUserPage === 1}
              options={userSelectOptions}
              onPopupScroll={(e) => {
                const { target } = e;
                if (target instanceof HTMLElement) {
                  if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
                    loadMoreUsers();
                  }
                }
              }}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {usersResponse?.hasMore && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '8px',
                      borderTop: '1px solid #f0f0f0',
                      color: '#666'
                    }}>
                      {isLoadingUsers ? (
                        'Loading more users...'
                      ) : (
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={loadMoreUsers}
                          style={{ padding: 0 }}
                        >
                          Load more users
                        </Button>
                      )}
                    </div>
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px', width: '100%' }}>
                    <Button
                      type="text"
                      icon={<UserAddOutlined />}
                      onClick={handleCreateNewUser}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        color: '#1890ff',
                        borderColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '8px'
                      }}
                    >
                      {t('users.createUser.button') || 'Create New User'}
                    </Button>
                  </Space>
                </>
              )}
              notFoundContent={
                isLoadingUsers || isSearching ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">Searching users...</Text>
                  </div>
                ) : allLoadedUsers.length === 0 && debouncedUserSearch ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">No users found for "{debouncedUserSearch}"</Text>
                  </div>
                ) : allLoadedUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">Start typing to search users</Text>
                  </div>
                ) : null
              }
            />
          </Form.Item>

          {/* Sección opcional de actualización de cuenta desde endpoint */}
          {panelEndpoints.length > 0 && (
            <Collapse
              ghost
              size="small"
              defaultActiveKey={['account-update']}
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: 'account-update',
                  label: (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t('loadRequest.create.updateAccountOptional') || 'Update account from endpoint (optional)'}
                    </Text>
                  ),
                  children: (
                    <div style={{ paddingTop: 8 }}>
                      <Form.Item
                        label={t('loadRequest.create.selectEndpoint') || 'Panel Endpoint'}
                        style={{ marginBottom: 12 }}
                      >
                        <Select
                          placeholder={t('loadRequest.create.selectEndpointPlaceholder') || 'Select an endpoint'}
                          value={selectedEndpointId}
                          onChange={(value) => {
                            setSelectedEndpointId(value);
                            // Auto-rellenar con datos del endpoint seleccionado
                            if (value) {
                              const endpoint = panelEndpoints.find(e => e.id === value);
                              if (endpoint) {
                                setNewAccountName(endpoint.name);
                                setNewAccountId(endpoint.id);
                              }
                            } else {
                              setNewAccountName('');
                              setNewAccountId('');
                            }
                          }}
                          allowClear
                          loading={isLoadingEndpoints}
                          options={panelEndpoints.map(endpoint => ({
                            value: endpoint.id,
                            label: (
                              <Space>
                                <Text>{endpoint.name}</Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  ({endpoint.status})
                                </Text>
                              </Space>
                            )
                          }))}
                        />
                      </Form.Item>

                      {selectedEndpointId && (
                        <div style={{
                          background: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          border: `1px solid ${theme === 'dark' ? '#333' : '#e1e1e1'}`,
                          padding: '12px',
                          borderRadius: '6px',
                          marginBottom: 0
                        }}>
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t('loadRequest.create.newAccountName') || 'Account Name'}: <Text strong>{newAccountName}</Text>
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t('loadRequest.create.newAccountId') || 'Account ID'}: <Text strong>{newAccountId}</Text>
                            </Text>
                          </Space>
                        </div>
                      )}
                    </div>
                  )
                }
              ]}
            />
          )}

          <Form.Item
            label={t('common.receipt') || 'Receipt'}
            required
          >
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t('loadRequest.create.uploadText') || 'Click or drag receipt file to upload'}
              </p>
              <p className="ant-upload-hint">
                {t('loadRequest.create.uploadHint') || 'Supports: JPG, PNG, PDF (Max 10MB)'}
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button onClick={handleCancel}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createLoadRequestMutation.isPending}
                icon={<UploadOutlined />}
                disabled={availablePanels.length > 1 && !selectedPanelId}
              >
                {t('loadRequest.create.submit') || 'Create Load Request'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <CreateUserModal
        open={createUserModalOpen}
        onCancel={() => {
          setCreateUserModalOpen(false);
          setPendingUsername(null);
        }}
        onSuccess={handleCreateUserSuccess}
      />
    </>
  );
};